import { Box, Button, Dialog, List, ListItem, Typography, makeStyles } from "@material-ui/core"
import DateInputField, { getCurrentDate } from "../ui/DateInputField";
import { useEffect, useState } from "react";
import useOrderApi, { FetchedOrder } from "../../api/OrderApi";
import useFormApi, { Form } from "../../api/FormApi";
import useMaterialApi, { Material } from "../../api/MaterialApi";
import { Identifiable } from "../../model/Items";
import useKitApi from "../../api/KitApi";
import useShapeApi, { Shape } from "../../api/ShapeApi";

type Props = {
  open: boolean;
  onDialogClosed: () => void;
}

interface KitElement extends Identifiable {
  quantity: number;
}

interface Kit extends Identifiable {
  forms: KitElement[];
  shapes: KitElement[];
}

type MaterialUtilization = {
  materialId: number;
  name: string;
  weight: number;
}

type Cache = {
  forms: Form[];
  shapes: Shape[];
  kits: Kit[];
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    minWidth: 640
  }
}));

const dateBetween = (date: string, startDate: string, endDate: string) => {
  return date.localeCompare(startDate) >= 0 && date.localeCompare(endDate) <= 0;
};

const OrderedMaterialsStatisticsDialog = (props: Props) => {
  const classes = useStyles();
  const materialApi = useMaterialApi();
  const formApi = useFormApi();
  const shapeApi = useShapeApi();
  const kitApi = useKitApi();
  const orderApi = useOrderApi();
  const [rangeStartDate, setRangeStartDate] = useState<string>();
  const [rangeEndDate, setRangeEndDate] = useState<string>();
  const [ordersFetchTime, setOrdersFetchTime] = useState<Date|null>(null);
  const [materials, setMaterials] = useState<Material[]|null>(null);
  const [materialUtilizations, setMaterialUtilizations] = useState<MaterialUtilization[]|null>(null);
  const [processedOrders, setProcessedOrders] = useState<FetchedOrder[]|null>(null);
  const [currentlyProcessedOrderIndex, setCurrentlyProcessedOrderIndex] = useState<number|null>(null);
  const [cachedForms, setCachedForms] = useState<Form[]>([]);
  const [cachedShapes, setCachedShapes] = useState<Shape[]>([]);
  const [cachedKits, setCachedKits] = useState<Kit[]>([]);

  const initializeProcessedOrders = (orders: FetchedOrder[]) => {
    const processedOrders = orders
      .sort((order, anotherOrder) => order.id - anotherOrder.id)
      .filter(orderCompletionDateInDateRange);
    setProcessedOrders(processedOrders);
  };

  const initializeMaterialUtilizations = (materials: Material[]) => {
    const materialUtilizations: MaterialUtilization[] = [];
    materials.forEach(material => {
      const materialId = material.id;
      materialUtilizations[materialId] = { materialId, name: material.name, weight: 0 };
    });
    setMaterialUtilizations(materialUtilizations);
  };

  const orderCompletionDateInDateRange = (order: FetchedOrder) => {
    const completionDate = order.completionDate;
    return completionDate !== undefined
      && rangeStartDate !== undefined
      && rangeEndDate !== undefined
      && dateBetween(completionDate, rangeStartDate, rangeEndDate);
  }

  const calculateOrderMaterialStatisticsAndIncrementCurrentlyProcessedOrderIndex = async (
    orders: FetchedOrder[],
    index: number
  ) => {
    const order = orders[index];
    if (materialUtilizations === null) {
      console.error(`### Material utilizations not initialized`);
    } else {
      const cache: Cache = {
        forms: cachedForms,
        shapes: cachedShapes,
        kits: cachedKits
      };
      const customerId = order.customer.id;
      const orderId = order.id;
      await calculateUtilizationForForms(materialUtilizations, cache, customerId, orderId);
      await calculateUtilizationForShapes(materialUtilizations, cache, customerId, orderId);
      await calculateUtilizationForKits(materialUtilizations, cache, customerId, orderId);
      setCachedForms(cache.forms);
      setCachedShapes(cache.shapes);
      setCachedKits(cache.kits);
      setMaterialUtilizations(materialUtilizations);
    }
    if (index < orders.length) {
      setCurrentlyProcessedOrderIndex(index + 1);
    }
  };

  const calculateUtilizationForForms = async (
    materialUtilizations: MaterialUtilization[],
    cache: Cache,
    customerId: number,
    orderId: number
  ) => {
    const orderForms = await orderApi.fetchOrderForms(customerId, orderId);
    for (const orderForm of orderForms) {
      const formId = orderForm.form.id;
      const form = await getForm(cache, customerId, formId);
      const materialId = form.material.id;
      const weight = form.weight;
      materialUtilizations[materialId].weight += orderForm.quantityDelivered * weight;
    }
    console.info(`### calculateOrderMaterialStatistics: [orderId = ${orderId}][customerId = ${customerId}] / forms: ${orderForms.length}`);
  }

  const calculateUtilizationForShapes = async (
    materialUtilizations: MaterialUtilization[],
    cache: Cache,
    customerId: number,
    orderId: number
  ) => {
    const orderShapes = await orderApi.fetchOrderShapes(customerId, orderId);
    for (const orderShape of orderShapes) {
      const shapeId = orderShape.shape.id;
      const shape = await getShape(cache, customerId, shapeId);
      const materialId = shape.material.id;
      const weight = shape.unitWeight;
      materialUtilizations[materialId].weight += orderShape.quantityDelivered * weight;
    }
    console.info(`### calculateOrderMaterialStatistics: [orderId = ${orderId}][customerId = ${customerId}] / shapes: ${orderShapes.length}`);
  };

  const calculateUtilizationForKits = async (
    materialUtilizations: MaterialUtilization[],
    cache: Cache,
    customerId: number,
    orderId: number
  ) => {
    const orderKits = await orderApi.fetchOrderKits(customerId, orderId);
    for (const orderKit of orderKits) {
      const kitId = orderKit.kit.id;
      const kitForms = await kitApi.fetchKitFormDefinitions(customerId, kitId);
      for (const kitForm of kitForms) {
        const quantity = orderKit.quantityDelivered * kitForm.quantity;
        const formId = kitForm.form.id;
        const form = await getForm(cache, customerId, formId);
        const materialId = form.material.id;
        const weight = form.weight;
        if (!weight) {
          console.info(`### No weight for form: ${formId}`);
        }
        if (!quantity) {
          console.info(`### No quantity for order kit form: ${orderKit.quantityDelivered} x ${kitForm.quantity}`);
        }
        if (!materialUtilizations[materialId]) {
          console.warn(`### No utilization found for material: ${materialId}`);
        }
        materialUtilizations[materialId].weight += quantity * weight;
      }
      const kitShapes = await kitApi.fetchKitShapeDefinitions(customerId, kitId);
      for (const kitShape of kitShapes) {
        const quantity = orderKit.quantityDelivered * kitShape.quantity;
        const shapeId = kitShape.shape.id;
        const shape = await getShape(cache, customerId, shapeId);
        const materialId = shape.material.id;
        const weight = shape.unitWeight;
        if (!weight) {
          console.info(`### No weight for shape: ${shapeId}`);
        }
        materialUtilizations[materialId].weight += quantity * weight;
      }
    }
    console.info(`### calculateOrderMaterialStatistics: [orderId = ${orderId}][customerId = ${customerId}] / kits: ${orderKits.length}`);
  };

  const getForm = async (cache: Cache, customerId: number, formId: number) => {
    const cachedForms = cache.forms;
    const cachedForm = cachedForms[formId];
    if (cachedForm === undefined) {
      const fetchedForm = await formApi.fetchForm(customerId, 0, formId); // TODO: hardcoded 0 as priceListId
      cachedForms[formId] = fetchedForm;
      return fetchedForm;
    } else {
      return cachedForm;
    }
  };

  const getShape = async (cache: Cache, customerId: number, shapeId: number) => {
    const cachedShapes = cache.shapes;
    const cachedShape = cachedShapes[shapeId];
    if (cachedShape === undefined) {
      const fetchedShape = await shapeApi.fetchShape(customerId, 0, shapeId); // TODO: hardcoded 0 as priceListId
      cachedShapes[shapeId] = fetchedShape;
      return fetchedShape;
    } else {
      return cachedShape;
    }
  };

  const getProcessingPercentage = () =>
    currentlyProcessedOrderIndex === null || processedOrders === null
      ? '?'
      : `${(100 * currentlyProcessedOrderIndex / processedOrders?.length).toFixed(0)}%`;

  const calculateTotalWeight = () =>
    (materialUtilizations || [])
      .map(materialUtilization => materialUtilization.weight)
      .reduce((accumulator, value) => accumulator + value, 0);

  const closeDialog = () => {
    props.onDialogClosed();
  };

  useEffect(() => {
    console.info(`## useEffect[1]: ordersFetchTime: ${ordersFetchTime}`);
    if (ordersFetchTime !== null) {
      setOrdersFetchTime(null);
      if (materials === null) {
        console.info(`## useEffect[1]: fetching materials`);
        materialApi
          .fetchMaterials()
          .then(setMaterials);
      } else {
        console.info(`## useEffect[1]: initializing material utilizations`);
        initializeMaterialUtilizations(materials);
      }
      orderApi
        .fetchOrders()
        .then(initializeProcessedOrders);
      setCurrentlyProcessedOrderIndex(null)
    }
  }, [ordersFetchTime]);

  useEffect(() => {
    if (materials !== null) {
      console.info(`## useEffect[2]: initializing material utilizations`);
      initializeMaterialUtilizations(materials);
    }
  }, [materials]);

  useEffect(() => {
    if (processedOrders !== null && processedOrders.length) {
      console.info(`## useEffect[3]: start processing orders`);
      setCurrentlyProcessedOrderIndex(0);
    }
  }, [processedOrders]);

  useEffect(() => {
    console.info(`## useEffect[4]: ${processedOrders?.length} : ${currentlyProcessedOrderIndex}`);
    if (processedOrders !== null && currentlyProcessedOrderIndex !== null) {
      if (currentlyProcessedOrderIndex < processedOrders.length) {
        calculateOrderMaterialStatisticsAndIncrementCurrentlyProcessedOrderIndex(processedOrders, currentlyProcessedOrderIndex);
      }
    }
  }, [processedOrders, currentlyProcessedOrderIndex]);

  return (
    <Dialog open={ props.open } maxWidth={ false }>
      <Box className={ classes.dialogContent }>
        <DateInputField
          initialValue={ getCurrentDate() }
          label='Początek okresu zestawienia wykorzystanych materiałów'
          onValueChange={ setRangeStartDate }
        />
        <DateInputField
          initialValue={ getCurrentDate() }
          label='Koniec okresu zestawienia wykorzystanych materiałów'
          onValueChange={ setRangeEndDate }
        />
        <Button
          color='primary'
          onClick={ () => setOrdersFetchTime(new Date()) }
          variant='contained'
        >
          Wylicz statystyki wykorzystania materiałów w zamówieniach w zadanym okresie
        </Button>
        <Button
          color='secondary'
          onClick={ () => closeDialog() }
          variant='contained'
        >
          Zamknij okno statystyk wykorzystania materiałów
        </Button>
      </Box>
      <Box paddingLeft={ 4 }>
      { currentlyProcessedOrderIndex === null
        ? <div>Brak zamówień spełniających kryteria</div>
        : <div>Przetwarzanie: { getProcessingPercentage() } ({currentlyProcessedOrderIndex} / {processedOrders?.length})</div>
      }
        <div>
          Waga całkowita:&nbsp;
          <Typography display='inline' style={ { fontWeight: 'bold' } }>
            { calculateTotalWeight().toFixed(3) }
          </Typography>
        </div>
      </Box>
      <List style={ { paddingLeft: 32 }}>
        { materialUtilizations !== null && [...materialUtilizations]
            .filter(materialUtilization => materialUtilization !== undefined)
            .filter(materialUtilization => materialUtilization.weight > 0)
            .sort((materialUtilization, anotherMaterialUtilization) => materialUtilization.name.localeCompare(anotherMaterialUtilization.name))
            .map(materialUtilization =>
                <ListItem key={`material-utilization-${materialUtilization.materialId}`}>
                  { materialUtilization.name } : { materialUtilization.weight.toFixed(3) }
                </ListItem>)
        }
      </List>
    </Dialog>
  );
};

export default OrderedMaterialsStatisticsDialog;